/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Flag,
  Info,
  ListChecks,
  Maximize2,
  Minimize2,
  Monitor,
  Navigation,
  Play,
  Send,
  Smartphone,
  Users,
} from "lucide-react";

import { ParticipantPreviewContentScreen } from "./ParticipantPreviewContentScreen";
import {
  buildExperimentPreviewUrl,
  normalizeExperimentPreviewRoute,
} from "../../config/previewUrl";
import { LIKERT_DEFAULT_OPTIONS } from "../../utils/feedbackFlow";
import {
  buildParticipantFlowSteps,
  getParticipantPreviewProgress,
  getPrototypeRoutes,
  hasCompletedResponse,
  isParticipantStepComplete,
  normalizePreviewRoute,
  type ParticipantFlowStep,
  type ParticipantQuestionStep,
  type PrototypeParticipantStep,
} from "../../utils/participantPreview";
import {
  isMessageFromPreviewFrame,
  navigatePreviewToRoute,
  scrollPreviewToElement,
} from "../../utils/previewMessaging";

import type {
  FeedbackFlow,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionResponse,
  FeedbackFlowResponseMap,
} from "../../types/experiment.types";

export type ParticipantPreviewMode = "route" | "embedded";

export type ParticipantPreviewSubmission = {
  questionId: string;
  section: FeedbackFlowQuestion["section"];
  source: FeedbackFlowQuestion["source"];
  question: string;
  taggedTarget: FeedbackFlowQuestion["taggedTarget"];
  response: FeedbackFlowQuestionResponse;
};

interface ParticipantPreviewProps {
  feedbackFlow: FeedbackFlow;
  initialRoute?: string;
  previewBaseUrl?: string;
  hasPrototypePreview?: boolean;
  mode: ParticipantPreviewMode;
  onBack: () => void;
  onFinish: (submissionPayload: ParticipantPreviewSubmission[]) => void;
}

const QUESTION_TYPE_LABEL: Record<FeedbackFlowQuestion["inputType"], string> = {
  text: "Free text",
  multiple_choice: "Multiple choice",
  checkbox: "Checkbox",
  likert_scale: "Likert scale",
  speech_to_text: "Speech to text",
};

function getStepIcon(step: ParticipantFlowStep) {
  switch (step.id) {
    case "intro":
      return FileText;
    case "personal_info":
      return ClipboardList;
    case "demographics":
      return Users;
    case "prototype":
      return ListChecks;
    case "outro":
      return Flag;
  }
}

function getStepEyebrow(step: ParticipantFlowStep): string {
  switch (step.id) {
    case "intro":
      return "Front Matter";
    case "personal_info":
      return "Participant Details";
    case "demographics":
      return "Background";
    case "prototype":
      return "General Questions";
    case "outro":
      return "End Matter";
  }
}

function getOptionToken(index: number, option: string): string {
  return `${index}::${option}`;
}

function isUniqueOption(options: string[], option: string): boolean {
  return options.indexOf(option) === options.lastIndexOf(option);
}

function decodeStoredOptionValue(value: string, options: string[]): string {
  const separatorIndex = value.indexOf("::");
  if (separatorIndex <= 0) {
    return value;
  }

  const rawIndex = value.slice(0, separatorIndex);
  const optionIndex = Number(rawIndex);
  if (!Number.isInteger(optionIndex) || optionIndex < 0 || optionIndex >= options.length) {
    return value.slice(separatorIndex + 2);
  }

  return options[optionIndex] ?? value.slice(separatorIndex + 2);
}

function normalizeAnswerForSubmission(
  question: FeedbackFlowQuestion,
  answer: FeedbackFlowQuestionResponse["answer"],
): FeedbackFlowQuestionResponse["answer"] {
  if (question.inputType === "multiple_choice") {
    if (Array.isArray(answer)) {
      return answer.map((value) => decodeStoredOptionValue(value, question.options));
    }

    if (typeof answer === "string") {
      return [decodeStoredOptionValue(answer, question.options)];
    }

    return answer;
  }

  if (question.inputType === "checkbox") {
    const singleAnswer = Array.isArray(answer) ? answer[0] : answer;

    if (typeof singleAnswer === "string") {
      return decodeStoredOptionValue(singleAnswer, question.options);
    }

    return singleAnswer;
  }

  if (Array.isArray(answer)) {
    return answer.map((value) => decodeStoredOptionValue(value, question.options));
  }

  if (typeof answer === "string") {
    return decodeStoredOptionValue(answer, question.options);
  }

  return answer;
}

export function ExperimentParticipantPreview({
  feedbackFlow,
  initialRoute = "/",
  previewBaseUrl,
  hasPrototypePreview = true,
  mode,
  onBack,
  onFinish,
}: ParticipantPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const contentViewportRef = useRef<HTMLDivElement>(null);
  const [responses, setResponses] = useState<FeedbackFlowResponseMap>({});
  const [activePrototypeQuestion, setActivePrototypeQuestion] = useState<string | null>(
    null,
  );
  const [showAllPages, setShowAllPages] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [introCompleted, setIntroCompleted] = useState(false);
  const [outroCompleted, setOutroCompleted] = useState(false);
  const [showPrototypeGuide, setShowPrototypeGuide] = useState(false);
  const [hasSeenPrototypeGuide, setHasSeenPrototypeGuide] = useState(false);
  const [prototypeViewport, setPrototypeViewport] = useState<"desktop" | "mobile">(
    "desktop",
  );
  const [isPrototypeFocused, setIsPrototypeFocused] = useState(false);

  const flowSteps = useMemo(
    () => buildParticipantFlowSteps(feedbackFlow),
    [feedbackFlow],
  );
  const prototypeStep = useMemo(
    () =>
      flowSteps.find(
        (step): step is PrototypeParticipantStep =>
          step.id === "prototype",
      ) ?? null,
    [flowSteps],
  );
  const prototypeQuestions = useMemo(
    () => prototypeStep?.questions ?? [],
    [prototypeStep],
  );
  const normalizeRouteForPreview = useCallback(
    (route: string) =>
      previewBaseUrl
        ? normalizeExperimentPreviewRoute(route, previewBaseUrl)
        : normalizePreviewRoute(route),
    [previewBaseUrl],
  );
  const [iframeRoute, setIframeRoute] = useState(() =>
    normalizeRouteForPreview(
      initialRoute || getPrototypeRoutes(prototypeQuestions)[0] || "/",
    ),
  );

  const currentStep = flowSteps[currentStepIndex];
  const currentQuestionStep =
    currentStep?.id === "personal_info" || currentStep?.id === "demographics"
      ? currentStep
      : null;
  const currentPrototypeStep = currentStep?.id === "prototype" ? currentStep : null;
  const isReviewerPreview = mode === "embedded";
  const prototypeRoutes = useMemo(
    () =>
      Array.from(
        new Set(
          getPrototypeRoutes(prototypeQuestions).map((route) => normalizeRouteForPreview(route)),
        ),
      ),
    [normalizeRouteForPreview, prototypeQuestions],
  );
  const progress = useMemo(
    () =>
      getParticipantPreviewProgress({
        feedbackFlow,
        responses,
        introCompleted,
        outroCompleted,
      }),
    [feedbackFlow, introCompleted, outroCompleted, responses],
  );
  const filteredPrototypeQuestions = useMemo(() => {
    if (showAllPages) {
      return prototypeQuestions;
    }

    return prototypeQuestions.filter((question) => {
      const route = question.taggedTarget?.route;
      return !route || normalizeRouteForPreview(route) === iframeRoute;
    });
  }, [iframeRoute, normalizeRouteForPreview, prototypeQuestions, showAllPages]);
  const currentPageCount = useMemo(
    () =>
      prototypeQuestions.filter((question) => {
        const route = question.taggedTarget?.route;
        return !route || normalizeRouteForPreview(route) === iframeRoute;
      }).length,
    [iframeRoute, normalizeRouteForPreview, prototypeQuestions],
  );
  const activeRouteIndex = prototypeRoutes.findIndex((route) => route === iframeRoute);

  useEffect(() => {
    if (currentStepIndex >= flowSteps.length) {
      setCurrentStepIndex(Math.max(flowSteps.length - 1, 0));
    }
  }, [currentStepIndex, flowSteps.length]);

  useEffect(() => {
    if (currentStep?.id !== "prototype") {
      setActivePrototypeQuestion(null);
      setShowAllPages(false);
      setIsPrototypeFocused(false);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep?.id === "prototype" && !hasSeenPrototypeGuide) {
      setShowPrototypeGuide(true);
      setHasSeenPrototypeGuide(true);
      return;
    }

    if (currentStep?.id !== "prototype") {
      setShowPrototypeGuide(false);
    }
  }, [currentStep, hasSeenPrototypeGuide]);

  useEffect(() => {
    contentViewportRef.current?.scrollTo({ top: 0 });
  }, [currentStepIndex]);

  useEffect(() => {
    if (prototypeRoutes.length === 0) {
      return;
    }

    if (!prototypeRoutes.includes(iframeRoute)) {
      setIframeRoute(prototypeRoutes[0]);
    }
  }, [iframeRoute, prototypeRoutes]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isMessageFromPreviewFrame(event, iframeRef.current)) {
        return;
      }

      if (event.data?.type === "iframe-url-change") {
        setIframeRoute(normalizeRouteForPreview(String(event.data.url || "/")));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [normalizeRouteForPreview]);

  const areQuestionsAnswered = useCallback(
    (questions: FeedbackFlowQuestion[]) =>
      questions.every((question) => hasCompletedResponse(responses[question.id])),
    [responses],
  );

  const setQuestionResponse = useCallback(
    (id: string, updater: (previous: FeedbackFlowQuestionResponse | undefined) => FeedbackFlowQuestionResponse) => {
      setResponses((previous) => ({
        ...previous,
        [id]: updater(previous[id]),
      }));
    },
    [],
  );

  const handleResponseChange = useCallback(
    (id: string, value: string) => {
      setQuestionResponse(id, (previous) => ({
        ...previous,
        answer: value,
      }));
    },
    [setQuestionResponse],
  );

  const handleMultiSelectToggle = useCallback(
    (id: string, token: string, fallbackValue: string) => {
      setQuestionResponse(id, (previous) => {
        const existing = previous?.answer;
        const values = Array.isArray(existing)
          ? existing
          : typeof existing === "string"
            ? [existing]
            : [];
        const nextValues = values.includes(token)
          ? values.filter((item) => item !== token && item !== fallbackValue)
          : [...values.filter((item) => item !== fallbackValue), token];

        return {
          ...previous,
          answer: nextValues,
        };
      });
    },
    [setQuestionResponse],
  );

  const handleSingleSelectCheckboxToggle = useCallback(
    (id: string, token: string, fallbackValue: string) => {
      setQuestionResponse(id, (previous) => {
        const existing = Array.isArray(previous?.answer)
          ? previous.answer[0]
          : previous?.answer;
        const isSelected = existing === token || existing === fallbackValue;

        return {
          ...previous,
          answer: isSelected ? undefined : token,
        };
      });
    },
    [setQuestionResponse],
  );

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((previous) => Math.min(previous + 1, flowSteps.length - 1));
  }, [flowSteps.length]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((previous) => Math.max(previous - 1, 0));
  }, []);

  const goToStepIndex = useCallback(
    (index: number) => {
      if (!isReviewerPreview) {
        return;
      }

      setCurrentStepIndex(Math.min(Math.max(index, 0), flowSteps.length - 1));
    },
    [flowSteps.length, isReviewerPreview],
  );

  const handleFinish = useCallback(() => {
    setOutroCompleted(true);

    const submissionPayload = feedbackFlow.questions.map((question) => ({
      questionId: question.id,
      section: question.section,
      source: question.source,
      question: question.question,
      taggedTarget: question.taggedTarget,
      response: {
        ...(responses[question.id] ?? {}),
        answer: normalizeAnswerForSubmission(
          question,
          responses[question.id]?.answer,
        ),
      },
    }));

    onFinish(submissionPayload);
  }, [feedbackFlow.questions, onFinish, responses]);

  const goToRoute = useCallback((route: string) => {
    const normalizedRoute = normalizeRouteForPreview(route);
    setIframeRoute(normalizedRoute);

    if (iframeRef.current && previewBaseUrl) {
      iframeRef.current.src = buildExperimentPreviewUrl(
        normalizedRoute,
        previewBaseUrl,
      );
    }
  }, [normalizeRouteForPreview, previewBaseUrl]);

  const goToPreviousRoute = useCallback(() => {
    if (activeRouteIndex <= 0) {
      return;
    }

    goToRoute(prototypeRoutes[activeRouteIndex - 1]);
  }, [activeRouteIndex, goToRoute, prototypeRoutes]);

  const goToNextRoute = useCallback(() => {
    if (activeRouteIndex < 0 || activeRouteIndex >= prototypeRoutes.length - 1) {
      return;
    }

    goToRoute(prototypeRoutes[activeRouteIndex + 1]);
  }, [activeRouteIndex, goToRoute, prototypeRoutes]);

  const scrollToElement = useCallback(
    (question: FeedbackFlowQuestion) => {
      if (!iframeRef.current || !question.taggedTarget?.selector || !previewBaseUrl) {
        setActivePrototypeQuestion(question.id);
        return;
      }

      setActivePrototypeQuestion(question.id);
      const targetRoute = question.taggedTarget.route
        ? normalizeRouteForPreview(question.taggedTarget.route)
        : null;

      if (targetRoute && targetRoute !== iframeRoute) {
        setIframeRoute(targetRoute);
        navigatePreviewToRoute(
          iframeRef.current,
          buildExperimentPreviewUrl(targetRoute, previewBaseUrl),
          question.taggedTarget.selector,
        );
        return;
      }

      scrollPreviewToElement(iframeRef.current, question.taggedTarget.selector);
    },
    [iframeRoute, normalizeRouteForPreview, previewBaseUrl],
  );

  const renderQuestionInput = (question: FeedbackFlowQuestion) => {
    const responseValue = responses[question.id]?.answer;
    const singleValue = Array.isArray(responseValue)
      ? responseValue[0] || ""
      : responseValue || "";
    const selectedValues = Array.isArray(responseValue)
      ? responseValue
      : question.inputType === "multiple_choice" && responseValue
        ? [responseValue]
        : [];
    const options =
      question.inputType === "likert_scale" && question.options.length === 0
        ? LIKERT_DEFAULT_OPTIONS
        : question.options;

    if (question.inputType === "text" || question.inputType === "speech_to_text") {
      return (
        <textarea
          value={singleValue}
          onChange={(event) => handleResponseChange(question.id, event.target.value)}
          placeholder={
            question.inputType === "speech_to_text"
              ? "Type spoken feedback..."
              : "Type your feedback..."
          }
          rows={3}
          className="w-full resize-none rounded-xl border border-white/10 bg-black/35 px-3 py-2 text-sm text-white placeholder-slate-500 transition-colors focus:border-[#D9B64B] focus:outline-none"
        />
      );
    }

    if (question.inputType === "multiple_choice") {
      return (
        <div className="space-y-2">
          {options.map((option, index) => {
            const optionToken = getOptionToken(index, option);
            const checked =
              selectedValues.includes(optionToken) ||
              (isUniqueOption(options, option) && selectedValues.includes(option));

            return (
              <label
                key={`${question.id}-checkbox-${index}`}
                className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors ${
                  checked
                    ? "border-[#D9B64B]/60 bg-[rgba(217,182,75,0.12)] text-white"
                    : "border-white/10 text-slate-300 hover:border-white/20"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    handleMultiSelectToggle(question.id, optionToken, option)
                  }
                  className="sr-only"
                />
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded ${
                    checked ? "border-[#D9B64B] bg-[#D9B64B]" : "border-slate-500"
                  } border-2`}
                >
                  {checked && <Check className="h-3 w-3 text-white" />}
                </div>
                {option}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.inputType === "checkbox") {
      return (
        <div className="space-y-2">
          {options.map((option, index) => {
            const optionToken = getOptionToken(index, option);
            const checked =
              singleValue === optionToken ||
              (isUniqueOption(options, option) && singleValue === option);

            return (
              <label
                key={`${question.id}-checkbox-${index}`}
                className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors ${
                  checked
                    ? "border-[#D9B64B]/60 bg-[rgba(217,182,75,0.12)] text-white"
                    : "border-white/10 text-slate-300 hover:border-white/20"
                }`}
              >
                <input
                  type="radio"
                  name={`preview-checkbox-${question.id}`}
                  checked={checked}
                  onChange={() =>
                    handleSingleSelectCheckboxToggle(question.id, optionToken, option)
                  }
                  className="sr-only"
                />
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                    checked ? "border-[#D9B64B]" : "border-slate-500"
                  }`}
                >
                  {checked && <div className="h-2 w-2 rounded-full bg-[#D9B64B]" />}
                </div>
                {option}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.inputType === "likert_scale") {
      return (
        <div className="flex flex-wrap gap-2">
          {options.map((option, index) => {
            const optionToken = getOptionToken(index, option);
            const selected =
              singleValue === optionToken ||
              (isUniqueOption(options, option) && singleValue === option);

            return (
              <button
                key={`${question.id}-likert-${index}`}
                type="button"
                onClick={() => handleResponseChange(question.id, optionToken)}
                className={`min-h-[76px] min-w-[132px] flex-1 rounded-2xl border px-3 py-3 text-left transition-colors ${
                  selected
                    ? "border-[#D9B64B]/70 bg-[linear-gradient(180deg,rgba(217,182,75,0.16),rgba(217,182,75,0.08))] text-[#F7E2A4] shadow-[inset_0_0_0_1px_rgba(217,182,75,0.12)]"
                    : "border-white/10 text-slate-300 hover:border-white/20"
                }`}
              >
                <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[#D9B64B]/65">
                  {index + 1}
                </span>
                <span className="mt-2 block text-base font-semibold leading-5">
                  {option}
                </span>
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {options.map((option, index) => {
          const optionToken = getOptionToken(index, option);
          const selected =
            singleValue === optionToken ||
            (isUniqueOption(options, option) && singleValue === option);

          return (
            <label
              key={`${question.id}-choice-${index}`}
              className={`flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors ${
                selected
                  ? "border-[#D9B64B]/60 bg-[rgba(217,182,75,0.12)] text-white"
                  : "border-white/10 text-slate-300 hover:border-white/20"
              }`}
            >
              <input
                type="radio"
                name={`preview-opt-${question.id}`}
                checked={selected}
                onChange={() => handleResponseChange(question.id, optionToken)}
                className="sr-only"
              />
              <div
                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                  selected ? "border-[#D9B64B]" : "border-slate-500"
                }`}
              >
                {selected && <div className="h-2 w-2 rounded-full bg-[#D9B64B]" />}
              </div>
              {option}
            </label>
          );
        })}
      </div>
    );
  };

  const allQuestionsAnswered = useMemo(
    () =>
      feedbackFlow.questions.every((question) =>
        hasCompletedResponse(responses[question.id]),
      ),
    [feedbackFlow.questions, responses],
  );

  const canAdvanceCurrentStep = useMemo(() => {
    if (!currentStep) {
      return false;
    }

    if (isReviewerPreview) {
      return true;
    }

    if (currentStep.id === "intro") {
      return true;
    }

    if (currentStep.id === "outro") {
      return allQuestionsAnswered;
    }

    return areQuestionsAnswered(currentStep.questions);
  }, [allQuestionsAnswered, areQuestionsAnswered, currentStep, isReviewerPreview]);

  const handleAdvanceCurrentStep = useCallback(() => {
    if (!currentStep || !canAdvanceCurrentStep) {
      return;
    }

    if (currentStep.id === "intro") {
      setIntroCompleted(true);
    }

    if (currentStep.id === "outro" || currentStepIndex === flowSteps.length - 1) {
      handleFinish();
      return;
    }

    goToNextStep();
  }, [
    canAdvanceCurrentStep,
    currentStep,
    currentStepIndex,
    flowSteps.length,
    goToNextStep,
    handleFinish,
  ]);

  const renderQuestionOptions = useCallback((question: FeedbackFlowQuestion) => {
    if (question.inputType === "likert_scale" && question.options.length === 0) {
      return LIKERT_DEFAULT_OPTIONS;
    }

    return question.options.filter((option) => option.trim());
  }, []);

  const renderQuestionStep = (step: ParticipantQuestionStep) => (
    <div className="flex min-h-full items-start justify-center px-6 py-8">
      <div className="w-full max-w-4xl rounded-[28px] border border-[#D9B64B]/18 bg-[linear-gradient(180deg,rgba(13,13,13,0.98),rgba(5,5,5,0.98))] p-8 shadow-[0_28px_90px_rgba(0,0,0,0.45)]">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#D9B64B]">
          {getStepEyebrow(step)}
        </p>
        <h2 className="mt-3 text-3xl font-semibold text-white">{step.title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-400">{step.description}</p>

        <div className="mt-8 space-y-6">
          {step.questions.map((question, index) => {
            const optionCount = renderQuestionOptions(question).length;

            return (
              <div
                key={question.id}
                className="rounded-[24px] border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(217,182,75,0.16)] text-xs font-semibold text-[#F3D98B]">
                    {index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-medium text-white">{question.question}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-300">
                        {QUESTION_TYPE_LABEL[question.inputType]}
                      </span>
                      {optionCount > 0 && (
                        <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
                          {optionCount} options
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {renderQuestionInput(question)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderPrototypeStep = () => (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-black">
      <div className="border-b border-white/10 bg-[#090909] px-6 py-4">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div />

          <div className="flex min-w-0 items-center justify-center gap-2">
            {prototypeRoutes.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={goToPreviousRoute}
                  disabled={activeRouteIndex <= 0}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                  title="Previous route"
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="no-scrollbar flex min-w-0 items-center gap-2 overflow-x-auto">
                  {prototypeRoutes.map((route, index) => {
                    const isActive = route === iframeRoute;

                    return (
                      <button
                        key={route}
                        type="button"
                        onClick={() => goToRoute(route)}
                        className={`h-9 min-w-9 rounded-xl border px-3 text-sm font-semibold transition-colors ${
                          isActive
                            ? "border-[#D9B64B] bg-[#D9B64B] text-black"
                            : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                        }`}
                        title={route}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={goToNextRoute}
                  disabled={
                    activeRouteIndex < 0 || activeRouteIndex >= prototypeRoutes.length - 1
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                  title="Next route"
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-3">
            <div className="flex items-center rounded-lg bg-white/5 p-0.5">
              <button
                type="button"
                onClick={() => setPrototypeViewport("desktop")}
                className={`flex h-8 w-9 items-center justify-center rounded-md transition-colors ${
                  prototypeViewport === "desktop"
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/80"
                }`}
                title="Desktop View"
              >
                <Monitor size={14} />
              </button>
              <button
                type="button"
                onClick={() => setPrototypeViewport("mobile")}
                className={`flex h-8 w-9 items-center justify-center rounded-md transition-colors ${
                  prototypeViewport === "mobile"
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/80"
                }`}
                title="Mobile View"
              >
                <Smartphone size={14} />
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsPrototypeFocused((current) => !current)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-slate-300 transition-colors hover:bg-white/10"
              title={isPrototypeFocused ? "Exit prototype-only view" : "Prototype-only view"}
            >
              {isPrototypeFocused ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <AnimatePresence>
          {showPrototypeGuide && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              className="absolute left-6 top-6 z-20 max-w-sm rounded-[24px] border border-[#D9B64B]/24 bg-[linear-gradient(180deg,rgba(10,10,10,0.97),rgba(3,3,3,0.98))] p-4 shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(217,182,75,0.12)] text-[#D9B64B]">
                  <Info className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D9B64B]">
                    Quick Guide
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-white">
                    How to answer product questions
                  </h3>
                  <ol className="mt-3 space-y-2 text-sm leading-6 text-slate-300">
                    <li>Explore the prototype in the center like a participant.</li>
                    <li>Use the right panel to open each saved product question.</li>
                    <li>Use `Scroll to element` or `Go to page` to jump to tagged areas.</li>
                    <li>
                      {isReviewerPreview
                        ? "Answers are optional in this reviewer preview."
                        : "Use Finish once every question has an answer."}
                    </li>
                  </ol>
                  <div className="mt-4 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPrototypeGuide(false)}
                      className="rounded-lg bg-[#D9B64B] px-3 py-1.5 text-sm font-medium text-black transition-colors hover:bg-[#E7C96C]"
                    >
                      Got it
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {hasPrototypePreview && previewBaseUrl ? (
          <div
            className={`flex h-full w-full items-center justify-center ${
              prototypeViewport === "mobile" ? "p-4" : "p-0"
            }`}
          >
            <div
              className={`relative flex h-full flex-col overflow-hidden transition-all duration-300 ease-in-out ${
                prototypeViewport === "mobile"
                  ? "w-[375px] rounded-[2.5rem] border-[8px] border-black bg-black shadow-2xl ring-1 ring-white/20"
                  : "w-full bg-transparent"
              }`}
            >
              <div
                className={`relative flex-1 overflow-hidden bg-white ${
                  prototypeViewport === "mobile" ? "rounded-[1.8rem]" : ""
                }`}
              >
                <iframe
                  ref={iframeRef}
                  src={buildExperimentPreviewUrl(iframeRoute, previewBaseUrl)}
                  className="absolute inset-0 h-full w-full border-0 bg-white"
                  title="Participant Preview"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center bg-[radial-gradient(circle_at_top,rgba(217,182,75,0.08),transparent_32%),linear-gradient(180deg,#080808_0%,#020202_100%)] px-6 text-center">
            <div className="max-w-md rounded-[28px] border border-[#D9B64B]/18 bg-[linear-gradient(180deg,rgba(13,13,13,0.98),rgba(5,5,5,0.98))] p-8 shadow-[0_28px_90px_rgba(0,0,0,0.45)]">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#D9B64B]">
                Prototype Preview
              </p>
              <h3 className="mt-3 text-2xl font-semibold text-white">
                Saved preview URL not available
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">
                The product questions loaded from the API, but the saved prototype
                deployment URL is not available yet for this iteration.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderProgressStepper = () => (
    <div
      className="border-b border-white/10 bg-[linear-gradient(180deg,rgba(10,10,10,0.96),rgba(3,3,3,0.98))] px-4 py-3"
      data-testid="participant-preview-stepper"
    >
      <div className="flex items-start gap-4">
        <div className="min-w-[140px]">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D9B64B]/85">
            Progress
          </p>
          <p className="mt-2 text-2xl font-semibold text-white">
            {progress.completedUnits}/{progress.totalUnits}
          </p>
          <p className="mt-1 text-xs text-slate-400">completed</p>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-800">
              <motion.div
                className="h-full rounded-full bg-[#D9B64B]"
                animate={{ width: `${progress.progressPct}%` }}
                transition={{ duration: 0.25 }}
              />
            </div>
            <div className="rounded-full border border-[#D9B64B]/30 bg-[rgba(217,182,75,0.08)] px-3 py-1 text-xs font-medium text-[#F3D98B]">
              {Math.round(progress.progressPct)}%
            </div>
          </div>

          <div className="no-scrollbar mt-3 flex items-center gap-2 overflow-x-auto pb-1">
            {flowSteps.map((step, index) => {
              const Icon = getStepIcon(step);
              const complete = isParticipantStepComplete(
                step,
                responses,
                introCompleted,
                outroCompleted,
              );
              const active = index === currentStepIndex;
              const stepperItemClassName = `flex min-w-[180px] shrink-0 items-center gap-3 rounded-[18px] border px-3 py-2 text-left transition-colors ${
                active
                  ? "border-[#D9B64B]/40 bg-[rgba(217,182,75,0.1)]"
                  : complete
                    ? "border-white/10 bg-white/[0.03]"
                    : "border-slate-800 bg-slate-950/50"
              } ${
                isReviewerPreview
                  ? "cursor-pointer hover:border-[#D9B64B]/30 hover:bg-[rgba(217,182,75,0.07)]"
                  : "cursor-default"
              }`;
              const stepperItemContent = (
                <>
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      complete
                        ? "bg-[#D9B64B] text-black"
                        : active
                          ? "bg-[rgba(217,182,75,0.14)] text-[#F3D98B]"
                          : "bg-slate-800 text-slate-500"
                    }`}
                  >
                    {complete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{step.title}</p>
                    <p className="truncate text-xs text-slate-400">{getStepEyebrow(step)}</p>
                  </div>
                </>
              );

              if (!isReviewerPreview) {
                return (
                  <div
                    key={step.id}
                    className={stepperItemClassName}
                    title={`${step.title} section`}
                  >
                    {stepperItemContent}
                  </div>
                );
              }

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => goToStepIndex(index)}
                  className={stepperItemClassName}
                  title={`Jump to ${step.title}`}
                >
                  {stepperItemContent}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const prototypeRailWidthClass = useMemo(() => {
    if (currentStep?.id !== "prototype") {
      return "w-[340px]";
    }

    if (activePrototypeQuestion) {
      return "w-[400px]";
    }

    if (filteredPrototypeQuestions.length <= 1) {
      return "w-[320px]";
    }

    if (filteredPrototypeQuestions.length <= 2) {
      return "w-[340px]";
    }

    return "w-[380px]";
  }, [activePrototypeQuestion, currentStep?.id, filteredPrototypeQuestions.length]);

  const renderPrototypeQuestionRail = () => (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] border border-[#D9B64B]/14 bg-[linear-gradient(180deg,rgba(10,10,10,0.96),rgba(3,3,3,0.98))]"
      data-testid="participant-preview-question-rail"
    >
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-white">Product Questions</span>
            <span className="rounded-full bg-[rgba(217,182,75,0.08)] px-2.5 py-1 text-xs text-[#F3D98B]">
              {currentPageCount}/{prototypeQuestions.length}
            </span>
          </div>
        </div>

        {prototypeRoutes.length > 1 && (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAllPages(false)}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors ${
                !showAllPages
                  ? "bg-[rgba(217,182,75,0.14)] text-[#F3D98B]"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              This page
            </button>
            <button
              type="button"
              onClick={() => setShowAllPages(true)}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors ${
                showAllPages
                  ? "bg-[rgba(217,182,75,0.14)] text-[#F3D98B]"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              All pages
            </button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4">
        {filteredPrototypeQuestions.length === 0 && (
          <div className="mt-8 rounded-xl border border-dashed border-slate-700 p-4 text-center">
            <p className="text-sm text-slate-400">No questions mapped to this state.</p>
            <p className="mt-1 text-xs text-slate-500">
              Switch routes or show all pages to review every question.
            </p>
          </div>
        )}

        {filteredPrototypeQuestions.map((question, index) => {
          const isActive = activePrototypeQuestion === question.id;
          const isAnswered = hasCompletedResponse(responses[question.id]);
          const optionCount = renderQuestionOptions(question).length;
          const isTagged = Boolean(
            question.taggedTarget?.selector || question.taggedTarget?.coordinates,
          );
          const questionRoute = question.taggedTarget?.route;
          const normalizedQuestionRoute = questionRoute
            ? normalizeRouteForPreview(questionRoute)
            : null;
          const isOtherPage = Boolean(
            normalizedQuestionRoute && normalizedQuestionRoute !== iframeRoute,
          );

          return (
            <div
              key={question.id}
              className={`overflow-hidden rounded-[24px] border transition-all duration-200 ${
                isActive
                  ? "border-[#D9B64B]/45 bg-[rgba(217,182,75,0.08)] shadow-lg shadow-black/30"
                  : isAnswered
                    ? "border-[#D9B64B]/18 bg-white/[0.03]"
                    : "border-slate-700/60 bg-slate-800/30"
              }`}
            >
              <div className="px-4 py-4">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setActivePrototypeQuestion((current) =>
                        current === question.id ? null : question.id,
                      )
                    }
                    className="group flex min-w-0 flex-1 items-start gap-3 text-left"
                  >
                    <div
                      className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                        isAnswered
                          ? "bg-[#D9B64B] text-black"
                          : "bg-slate-700 text-slate-300"
                      }`}
                    >
                      {isAnswered ? <Check className="h-3.5 w-3.5" /> : index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`break-words text-sm leading-6 ${
                          isActive ? "font-medium text-white" : "text-slate-200"
                        }`}
                      >
                        {question.question || "No question set"}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-300">
                          {QUESTION_TYPE_LABEL[question.inputType]}
                        </span>
                        {optionCount > 0 && (
                          <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
                            {optionCount} options
                          </span>
                        )}
                        {isTagged && (
                          <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-emerald-300">
                            Tagged
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActivePrototypeQuestion((current) =>
                        current === question.id ? null : question.id,
                      )
                    }
                    className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-300"
                    aria-label={isActive ? "Collapse question" : "Expand question"}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isActive ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {(questionRoute || isTagged) && (
                  <div className="mt-3 ml-11 flex flex-wrap items-center gap-3">
                    {isTagged && (
                      <button
                        type="button"
                        onClick={() => scrollToElement(question)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#D9B64B] transition-colors hover:text-[#F3D98B]"
                      >
                        {isOtherPage ? (
                          <>
                            <Navigation className="h-3.5 w-3.5" />
                            Go to page
                          </>
                        ) : (
                          <>
                            <Navigation className="h-3.5 w-3.5" />
                            Scroll to element
                          </>
                        )}
                      </button>
                    )}
                    {questionRoute && (
                      <span className="max-w-full truncate text-xs text-slate-500">
                        {questionRoute}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-1">
                      <div className="rounded-2xl bg-black/35 p-4">
                        {renderQuestionInput(question)}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderCurrentSectionRail = () => {
    if (!currentStep) {
      return null;
    }

    if (currentStep.id === "intro" || currentStep.id === "outro") {
      return (
        <div className="flex max-h-full w-full self-start min-h-0 flex-col overflow-hidden rounded-[28px] border border-[#D9B64B]/14 bg-[linear-gradient(180deg,rgba(10,10,10,0.96),rgba(3,3,3,0.98))]">
          <div className="border-b border-white/10 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D9B64B]/85">
              Current Section
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-white">{currentStep.title}</h3>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
            <p className="text-sm leading-6 text-slate-400">{currentStep.description}</p>
            {currentStep.terms && currentStep.terms.trim() && (
              <div className="mt-5 rounded-xl border border-white/10 bg-black/35 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Terms
                </p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-400">
                  {currentStep.terms}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] border border-[#D9B64B]/14 bg-[linear-gradient(180deg,rgba(10,10,10,0.96),rgba(3,3,3,0.98))]">
        <div className="border-b border-white/10 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D9B64B]/85">
            Current Section
          </p>
          <h3 className="mt-3 text-2xl font-semibold text-white">{currentStep.title}</h3>
          <p className="mt-3 text-sm leading-6 text-slate-400">{currentStep.description}</p>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-5 py-4">
          {currentStep.questions.map((question, index) => {
            const answered = hasCompletedResponse(responses[question.id]);
            const optionCount = renderQuestionOptions(question).length;

            return (
              <div
                key={question.id}
                className="rounded-[22px] border border-white/10 bg-black/35 px-4 py-3"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      answered ? "bg-[#D9B64B] text-black" : "bg-slate-800 text-slate-400"
                    }`}
                  >
                    {answered ? <Check className="h-3.5 w-3.5" /> : index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white">{question.question}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-300">
                        {QUESTION_TYPE_LABEL[question.inputType]}
                      </span>
                      {optionCount > 0 && (
                        <span className="rounded-full border border-white/10 bg-black/25 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
                          {optionCount} options
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (flowSteps.length === 0) {
    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 px-6"
        data-testid="participant-preview-overlay"
      >
        <div className="max-w-md rounded-3xl border border-[#D9B64B]/18 bg-[linear-gradient(180deg,rgba(14,14,14,0.98),rgba(5,5,5,0.98))] p-8 text-center text-white">
          <h1 className="text-xl font-semibold">No feedback flow configured</h1>
          <p className="mt-3 text-sm text-slate-400">
            Return to Step 4 and add front matter, participant questions, or product questions.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#D9B64B] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#E7C96C]"
          >
            <ArrowLeft className="h-4 w-4" />
            {mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex flex-col bg-black"
      data-testid="participant-preview-overlay"
    >
      <div className="flex h-16 flex-shrink-0 items-center justify-between border-b border-white/10 bg-[linear-gradient(90deg,#070707_0%,#0B0B0B_52%,#070707_100%)] px-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-lg border border-[#D9B64B]/18 bg-white/5 px-3 py-1.5 text-sm font-medium text-slate-100 transition-colors hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 text-[#D9B64B]" />
            {mode === "embedded" ? "Back to Tagging" : "Back to Innovation"}
          </button>
          <div className="h-5 w-px bg-slate-800" />
          <div className="flex items-center gap-2">
            <Play className="h-4 w-4 text-[#D9B64B]" />
            <span className="text-sm font-medium text-white">Participant Preview</span>
          </div>
        </div>

        <div className="mx-6 min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#D9B64B]/75">
            Current Section
          </p>
          <p className="mt-1 truncate text-lg font-semibold text-white">
            {currentStep?.title ?? "Participant Preview"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={goToPreviousStep}
            disabled={currentStepIndex === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <button
            type="button"
            onClick={handleAdvanceCurrentStep}
            disabled={!canAdvanceCurrentStep}
            data-testid="participant-preview-header-next"
            className="inline-flex items-center gap-2 rounded-lg bg-[#D9B64B] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#E7C96C] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
          >
            {currentStepIndex === flowSteps.length - 1 ? "Finish" : "Next"}
            {currentStepIndex === flowSteps.length - 1 ? (
              <Send className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!(currentStep?.id === "prototype" && isPrototypeFocused) && renderProgressStepper()}

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div
            ref={contentViewportRef}
            className={`flex min-w-0 flex-1 flex-col ${
              currentStep?.id === "prototype" ? "overflow-hidden" : "overflow-y-auto"
            } bg-[radial-gradient(circle_at_top,rgba(217,182,75,0.12),transparent_28%),linear-gradient(180deg,#080808_0%,#020202_100%)]`}
          >
            {currentStep?.id === "intro" && (
              <ParticipantPreviewContentScreen
                eyebrow={getStepEyebrow(currentStep)}
                title={currentStep.title}
                description={currentStep.description}
                terms={currentStep.terms}
              />
            )}

            {currentQuestionStep && renderQuestionStep(currentQuestionStep)}

            {currentPrototypeStep && renderPrototypeStep()}

            {currentStep?.id === "outro" && (
              <ParticipantPreviewContentScreen
                eyebrow={getStepEyebrow(currentStep)}
                title={currentStep.title}
                description={currentStep.description}
                terms={currentStep.terms}
                centered
              />
            )}
          </div>

          {!(currentStep?.id === "prototype" && isPrototypeFocused) && (
            <aside
              className={`flex h-full flex-shrink-0 overflow-hidden border-l border-white/10 bg-[linear-gradient(180deg,#050505_0%,#000000_100%)] px-4 py-4 ${
                prototypeRailWidthClass
              }`}
            >
              {currentStep?.id === "prototype"
                ? renderPrototypeQuestionRail()
                : renderCurrentSectionRail()}
            </aside>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default ExperimentParticipantPreview;
